<template>
  <Teleport to="body">
    <div class="fixed top-4 right-4 z-50 space-y-2">
      <TransitionGroup
        name="toast"
        tag="div"
        class="space-y-2"
      >
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="bg-white rounded-xl shadow-lg border border-gray-200/50 overflow-hidden min-w-80 max-w-md transform transition-all duration-300"
          :class="getToastClasses(toast.type)"
        >
          <div class="p-4">
            <div class="flex items-start gap-3">
              <!-- 图标 -->
              <div 
                class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                :class="getIconClasses(toast.type)"
              >
                <component :is="getIcon(toast.type)" class="w-4 h-4 text-white" />
              </div>
              
              <!-- 内容 -->
              <div class="flex-1 min-w-0">
                <h4 class="text-sm font-medium text-gray-900 mb-1">
                  {{ toast.title }}
                </h4>
                <p v-if="toast.message" class="text-sm text-gray-600">
                  {{ toast.message }}
                </p>
              </div>
              
              <!-- 关闭按钮 -->
              <button
                @click="removeToast(toast.id)"
                class="text-gray-400 hover:text-gray-600 p-1 rounded-md transition-colors flex-shrink-0"
              >
                <X class="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <!-- 进度条 -->
          <div 
            v-if="toast.duration && toast.duration > 0"
            class="h-1 bg-gray-200"
          >
            <div 
              class="h-full transition-all ease-linear"
              :class="getProgressClasses(toast.type)"
              :style="{ 
                width: `${getProgress(toast)}%`,
                transitionDuration: `${toast.duration}ms`
              }"
            ></div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { X, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-vue-next'

export interface ToastItem {
  id: string
  type: 'success' | 'warning' | 'error' | 'info'
  title: string
  message?: string
  duration?: number
  createdAt: number
}

const toasts = ref<ToastItem[]>([])

const getIcon = (type: ToastItem['type']) => {
  const icons = {
    success: CheckCircle,
    warning: AlertTriangle,
    error: XCircle,
    info: Info
  }
  return icons[type]
}

const getIconClasses = (type: ToastItem['type']) => {
  const classes = {
    success: 'bg-gradient-to-br from-green-500 to-green-600',
    warning: 'bg-gradient-to-br from-yellow-500 to-yellow-600',
    error: 'bg-gradient-to-br from-red-500 to-red-600',
    info: 'bg-gradient-to-br from-blue-500 to-blue-600'
  }
  return classes[type]
}

const getToastClasses = (type: ToastItem['type']) => {
  const classes = {
    success: 'border-l-4 border-green-500',
    warning: 'border-l-4 border-yellow-500',
    error: 'border-l-4 border-red-500',
    info: 'border-l-4 border-blue-500'
  }
  return classes[type]
}

const getProgressClasses = (type: ToastItem['type']) => {
  const classes = {
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500',
    info: 'bg-blue-500'
  }
  return classes[type]
}

const getProgress = (toast: ToastItem) => {
  if (!toast.duration || toast.duration <= 0) return 0
  const elapsed = Date.now() - toast.createdAt
  const progress = Math.max(0, 100 - (elapsed / toast.duration) * 100)
  return progress
}

const addToast = (toast: Omit<ToastItem, 'id' | 'createdAt'>) => {
  const newToast: ToastItem = {
    ...toast,
    id: Math.random().toString(36).substr(2, 9),
    createdAt: Date.now()
  }
  
  toasts.value.push(newToast)
  
  // 自动移除
  if (toast.duration && toast.duration > 0) {
    setTimeout(() => {
      removeToast(newToast.id)
    }, toast.duration)
  }
}

const removeToast = (id: string) => {
  const index = toasts.value.findIndex(toast => toast.id === id)
  if (index > -1) {
    toasts.value.splice(index, 1)
  }
}

const clearAll = () => {
  toasts.value = []
}

// 便捷方法
const success = (title: string, message?: string, duration = 4000) => {
  addToast({ type: 'success', title, message, duration })
}

const warning = (title: string, message?: string, duration = 5000) => {
  addToast({ type: 'warning', title, message, duration })
}

const error = (title: string, message?: string, duration = 6000) => {
  addToast({ type: 'error', title, message, duration })
}

const info = (title: string, message?: string, duration = 4000) => {
  addToast({ type: 'info', title, message, duration })
}

defineExpose({
  addToast,
  removeToast,
  clearAll,
  success,
  warning,
  error,
  info
})
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>
